import React, { useEffect, useState } from "react";
import SceneBuilder from "./SceneBuilder";
import IsometricBuilder from "./IsometricBuilder";
import IconShowcase from "./IconShowcase";
import { listLocalProfiles, makeLocalRef, makeServerRef, writeActiveRef } from "./lib/profileStore";

type ViewId = "scene" | "iso" | "icon";

const VIEW_KEY = "oneqode-active-view";
const VIEWS: { id: ViewId; label: string }[] = [
  { id: "scene", label: "Scene" },
  { id: "iso",   label: "Isometric" },
  { id: "icon",  label: "3D Icon" },
];

// Hash-based routing keeps the URL shareable without needing a server-side
// history-API fallback (works under any ONEQODE_BASE subpath, file://, etc.).
// Canonical forms:
//   #/scene
//   #/iso
//   #/icon
//   #/iso/<slug>          → switch to view AND load that profile (server
//                           slug like "iso-home" or "iso-main"; or a
//                           local slug if it starts with "local-")
// Trailing slashes / query strings are tolerated.

function parseViewFromHash(hash: string): ViewId | null {
  const m = hash.replace(/^#\/?/, "").split(/[/?]/)[0]?.toLowerCase();
  if (m === "iso" || m === "scene" || m === "icon") return m;
  return null;
}

function parseProfileSlugFromHash(hash: string): string | null {
  const parts = hash.replace(/^#\/?/, "").split(/[/?]/);
  const slug = parts[1]?.toLowerCase();
  return slug && /^[a-z0-9][a-z0-9-]*$/.test(slug) ? slug : null;
}

/** If the URL carries a profile slug for the active view, write it into
 *  localStorage as the active ref BEFORE the builder mounts (its
 *  useState reads this on the first render). Returns true if anything
 *  was applied. */
function applyHashProfile(view: ViewId, hash: string): boolean {
  if (view === "icon") return false; // 3D icon view has no profiles
  const slug = parseProfileSlugFromHash(hash);
  if (!slug) return false;
  // Anything starting with "local-" → local source; anything else
  // (e.g. "iso-home", "scene-main") → server source.
  const source: "local" | "server" = slug.startsWith("local-") ? "local" : "server";
  if (source === "local") {
    // Hydrate name from the saved local entry so the autosave path
    // doesn't subsequently overwrite the entry's name with an empty
    // string. If the slug isn't found locally (e.g. user opened a
    // shared link to a profile they don't have), fall through without
    // writing — the builder will boot with the previous active ref.
    const entry = listLocalProfiles(view === "scene" ? "scene" : "iso")
      .find((p) => p.slug === slug);
    if (!entry) return false;
    writeActiveRef(makeLocalRef(view, slug, entry.name));
    return true;
  }
  // Server slug — name is resolved by the picker from the slug.
  writeActiveRef(makeServerRef(view, slug, ""));
  return true;
}

function readInitialView(): ViewId {
  const fromHash = parseViewFromHash(window.location.hash);
  if (fromHash) {
    applyHashProfile(fromHash, window.location.hash);
    return fromHash;
  }
  try {
    const saved = localStorage.getItem(VIEW_KEY);
    if (saved === "iso" || saved === "scene" || saved === "icon") return saved;
  } catch {}
  return "scene";
}

export default function App() {
  const [view, setView] = useState<ViewId>(readInitialView);

  // Persist + reflect into the URL hash. Only writes when the value changes
  // so we don't churn history entries on first mount.
  useEffect(() => {
    try { localStorage.setItem(VIEW_KEY, view); } catch {}
    // Preserve the existing profile slug only if it matches the new
    // view — slugs are prefixed with the view kind ("iso-home",
    // "scene-main"), so cross-view slugs would point at a missing
    // profile. Local slugs ("local-…") are preserved regardless.
    const slug = parseProfileSlugFromHash(window.location.hash);
    const slugBelongsToView = slug && (
      slug.startsWith(`${view}-`) || slug.startsWith("local-")
    );
    const desired = slugBelongsToView && view !== "icon"
      ? `#/${view}/${slug}`
      : `#/${view}`;
    if (window.location.hash !== desired) {
      const url = `${window.location.pathname}${window.location.search}${desired}`;
      window.history.replaceState(null, "", url);
    }
  }, [view]);

  // Mirror builder profile switches into the URL hash so the link is
  // always shareable. profileStore.writeActiveRef dispatches a custom
  // event with the new ref; we encode it as the second hash segment.
  useEffect(() => {
    const onActiveRef = (e: Event) => {
      const ref = (e as CustomEvent).detail as { viewKind: ViewId; slug: string } | undefined;
      if (!ref || ref.viewKind === "icon") return;
      // Only mirror the profile of the *currently visible* view.
      if (ref.viewKind !== view) return;
      const desired = `#/${ref.viewKind}/${ref.slug}`;
      if (window.location.hash !== desired) {
        const url = `${window.location.pathname}${window.location.search}${desired}`;
        window.history.replaceState(null, "", url);
      }
    };
    window.addEventListener("oneqode:active-ref", onActiveRef);
    return () => window.removeEventListener("oneqode:active-ref", onActiveRef);
  }, [view]);

  // Listen for hash changes (back/forward, manual edit, paste-link in same tab).
  useEffect(() => {
    const onHashChange = () => {
      const next = parseViewFromHash(window.location.hash);
      if (next) {
        // A pasted link can carry a profile slug too; honour it.
        applyHashProfile(next, window.location.hash);
        if (next !== view) setView(next);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [view]);

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      {view === "scene" && <SceneBuilder />}
      {view === "iso" && <IsometricBuilder />}
      {view === "icon" && <IconShowcase />}
      <div
        style={{
          position: "absolute",
          top: 12,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 4,
          background: "rgba(15, 15, 18, 0.86)",
          border: "1px solid #1f1f24",
          borderRadius: 4,
          padding: 3,
          pointerEvents: "auto",
          zIndex: 50,
        }}
      >
        {VIEWS.map((v) => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            style={{
              background: view === v.id ? "#0b72b7" : "transparent",
              color: "#e7e7eb",
              border: "none",
              padding: "4px 12px",
              borderRadius: 3,
              fontSize: 11,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}
